import { i18n } from '@goodboydigital/astro';
import gsap from 'gsap';
import { Container, Sprite, Text, Texture } from 'pixi.js';
import { SignalConnection } from 'typed-signals';

import { App } from '../../App';
import { CloseButton } from '../buttons/CloseButton';
import { RectangleButton } from '../buttons/RectangleButton';
import LocalisedText from '../LocalisedText';
import { Panel } from '../Panel';

const defaultOpts = {
    title: '',
    titleParams: undefined as any,
    okButton: '',
    width: 800,
    height: 500,
    shining: false,
    shade: true,
};

export type PopupOptions = typeof defaultOpts;

export class Popup
{
    public app: App;
    public view: Container;

    protected container: Container;
    protected divLeft: Container;
    protected divRight: Container;
    protected divMid: Container;
    protected panel: Panel;
    protected shade: Sprite;
    protected title: LocalisedText;
    protected closeButton: CloseButton;
    protected okButton?: RectangleButton;
    protected presenting = false;
    protected options: PopupOptions;
    protected resizeConnection?: SignalConnection;

    constructor(app: App, opts: Partial<PopupOptions> = {})
    {
        this.app = app;
        this.view = new Container();
        this.options = { ...defaultOpts, ...opts };

        this.shade = new Sprite(Texture.WHITE);
        this.shade.tint = 0x000000;
        this.shade.interactive = true;
        this.shade.anchor.set(0.5);
        this.shade.alpha = this.options.shade ? 0.9 : 0.0;
        this.view.addChild(this.shade);

        this.container = new Container();
        this.view.addChild(this.container);

        this.panel = new Panel({ w: this.options.width, h: this.options.height });
        this.container.addChild(this.panel.view);
        if (this.options.shining) this.panel.turnOnShining();

        // Left side container
        this.divLeft = new Container();
        this.container.addChild(this.divLeft);
        this.divLeft.x = -this.options.width * 0.25;
        this.divLeft.y = 20;

        // Right side container
        this.divRight = new Container();
        this.container.addChild(this.divRight);
        this.divRight.x = -this.divLeft.x;
        this.divRight.y = this.divLeft.y;

        // Middle container
        this.divMid = new Container();
        this.container.addChild(this.divMid);
        this.divMid.x = 0;
        this.divMid.y = this.divLeft.y;

        this.title = new LocalisedText('', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 60,
            maxWidth: this.options.width * 0.8
        });
        this.title.anchor.set(0.5);
        this.title.y = (-this.panel.bg.height / 2) + 50;
        this.container.addChild(this.title);
        this.title.setText(this.options.title, this.options.titleParams);

        if (this.options.okButton)
        {
            this.okButton = new RectangleButton({
                text: this.options.okButton,
                fontSize: 50,
                bg: 'magenta',
                w: 300,
                h: 100,
                startHidden: true,
            });
            this.view.addChild(this.okButton.view);
            this.okButton.onPress.connect(() => this.dismiss());
        }

        this.closeButton = new CloseButton({
            startHidden: true,
        });
        this.view.addChild(this.closeButton.view);
        this.closeButton.onPress.connect(() => this.dismiss());

        this.resize();
    }

    public async present(): Promise<void>
    {
        if (this.presenting) return;
        this.presenting = true;
        this.onPresent();

        this.app.stage.stage.addChild(this.view);
        this.resizeConnection = this.app.resize.onResize.connect(() => this.resize());
        this.resize();

        this.killTweens();

        this.container.scale.set(0.5);
        this.container.alpha = 1;
        if (this.okButton) this.okButton.view.scale.set(0);
        this.closeButton.view.scale.set(0);
        this.shade.alpha = 0.00001;

        if (this.options.shade) gsap.to(this.shade, { alpha: 0.75, duration: 0.2 });
        await gsap.to(this.container.scale, { x: 1, y: 1, duration: 0.3, ease: 'back.out' });

        if (this.okButton) await this.okButton.show();
        await this.closeButton.show();
    }

    public async dismiss(): Promise<void>
    {
        if (!this.presenting) return;

        this.presenting = false;
        this.killTweens();

        if (this.resizeConnection) this.resizeConnection.disconnect();
        this.resizeConnection = undefined;

        if (this.okButton) this.okButton.hide();
        this.closeButton.hide();
        gsap.to(this.container.scale, { x: 1.5, y: 1.5, duration: 0.3, ease: 'back.in' });
        gsap.to(this.container, { alpha: 0, duration: 0.3, ease: 'sine.in' });
        await gsap.to(this.shade, { alpha: 0, duration: 0.2, delay: 0.1 });

        if (this.view.parent) this.view.parent.removeChild(this.view);
        this.onDismiss();
    }

    public resize(): void
    {
        this.view.x = this.app.resize.width / 2;
        this.view.y = this.app.resize.height / 2;

        this.shade.width = this.app.resize.width;
        this.shade.height = this.app.resize.height;

        if (this.okButton)
        {
            this.okButton.view.x = 0;
            this.okButton.view.y = this.panel.bg.height / 2;
        }

        this.closeButton.view.x = (this.panel.bg.width / 2) - 20;
        this.closeButton.view.y = (-this.panel.bg.height / 2) + 20;
    }

    protected killTweens(): void
    {
        gsap.killTweensOf(this.shade);
        if (this.okButton) gsap.killTweensOf(this.okButton.view);
        gsap.killTweensOf(this.closeButton.view);
        gsap.killTweensOf(this.container);
        gsap.killTweensOf(this.container.scale);
    }

    protected onPresent(): void
    {
        // For subclasses
    }

    protected onDismiss(): void
    {
        // For subclasses
    }
}
